<script>
  import { ColorLegend, SVGMap, SVGPolygonLayer } from "@urbaninstitute/dataviz-components/maps";
  import { Tooltip, LoadingWrapper, Block } from "@urbaninstitute/dataviz-components";
  import { urbanColors } from "@urbaninstitute/dataviz-components/utils";
  import * as topojson from "topojson-client";
  import { StateData, getStateName } from "$utils/states.js";
  import { geoAlbersUsaPr } from "$utils/projection.js";
  import Tilemap from "$lib/components/Tilemap/Tilemap.svelte";
  import { getConfig } from "$lib/utils/config.js";
  import { getScale } from "$lib/utils/colors.js";

  /** @typedef {import("$utils/states").FeatureOptions} FeatureOptions */

  /**
   * @typedef {Object} Tooltip
   * @property {number} x
   * @property {number} y
   * @property {Record<string, any>} props
   */

  /**
   * @typedef {Object} Props
   * @property {Object[]} data
   * @property {string} metric
   * @property {string} currentState
   * @property {(e: CustomEvent<{props: {map_id: string}}>) => void} onStateSelect
   * @property {import('$lib/types').mapType} [mapType]
   * @property {import("$lib/utils/colors").ScaleType} scaleType
   * @property {import("$lib/utils/colors").ColorPalette} [colorPalette="blue"]
   * @property {number[] | undefined} [scaleBreaks]
   * @property {[number, number] | undefined} [minMax]
   * @property {FeatureOptions} [featureOption="states"]
   * @property {string} [naColor="#cecece"]
   * @property {string[] | undefined} [tooltipColumns]
   */

  /** @type {Props} */
  let {
    data,
    metric,
    currentState = $bindable(),
    onStateSelect,
    mapType,
    scaleType,
    colorPalette = "blue",
    scaleBreaks = undefined,
    minMax = undefined,
    featureOption = "states",
    naColor = "#cecece",
    tooltipColumns
  } = $props();

  const config = getConfig()();

  const stateData = new StateData();

  /** @type { string } */
  /** @type { string   | undefined } */
  /** @type { Tooltip   | undefined } */
  let tooltip = $state();
  const scaleValues = $derived(data.map((d) => d[metric]));
  const currentColorScale = $derived(
    scaleBreaks
      ? getScale(scaleType, colorPalette, scaleValues, scaleBreaks)
      : getScale(scaleType, colorPalette, scaleValues, undefined, minMax)
  );

  // const geoData = topojson.feature(topoData, "states");
  let geoData = stateData.getFeatures(featureOption);

  async function joinData(geo, d) {
    const geoData = await geo;
    return geoData.map((place) => {
      let result = {
        type: place.type,
        geometry: place.geometry,
        properties: place.properties
      };
      const match = d.find((row) => row.map_id === place.properties.fips);
      if (match) {
        result.properties = Object.assign({}, result.properties, match);
      } else {
        // if there is no join data, fill the expected columns with N/A
        let missingProps = {};
        config.columns.keys().forEach((col) => {
          if (!result.properties[col]) {
            missingProps[col] = "N/A";
          }
        });
        result.properties = Object.assign({}, result.properties, missingProps);
      }
      return result;
    });
  }

  function handleStateClick(e) {
    onStateSelect(e);
    // logClickToGA(e.target, "tilemap-state-click--" + e.detail.props.name);
  }

  function handleStateHover(e) {
    tooltip = getTooltipProps(e.detail.e.pageX, e.detail.e.pageY, e.detail.props);
  }

  function onClear(e) {
    currentState = "";
    onStateSelect();
    // dispatch("stateSelect", currentState);
  }

  /**
   * @param {number} x
   * @param {number} y
   * @param {Record<string, any>} props
   * @returns {Tooltip}
   */
  function getTooltipProps(x, y, props) {
    // add in state name if not present
    if (props.map_id && !props.state) {
      props.state = getStateName(props.map_id);
    }
    return {
      x,
      y,
      props
    };
  }

  /**
   * Raise a dom node to top of siblings
   * @param { HTMLElement } el The DOM element to raise
   * @returns void
   */
  export function raise(el) {
    if (el.parentNode) {
      el.parentNode.appendChild(el);
    }
  }

  const joinedData = $derived(joinData(geoData, data));

  function fillFunc(feature, metric) {
    return currentColorScale(feature[metric]);
  }
</script>

<Block>
<div style="max-width: 300px; margin: 1em auto 0 0;">
  <!-- <div class="legend-labels"><span>Smaller gap</span><span>Larger gap</span></div> -->
  <ColorLegend
    scale={currentColorScale}
    height={16}
    tickLineColor={"white"}
    tickLineWidth={2}
    tickFormat={config.columns.get(metric).format}
    margin={{ top: 0, right: 5, bottom: 5, left: 0 }}
  />
</div>
</Block>
<div class="tile-map">
  {#if mapType == "geo"}
    {#await joinedData}
      <LoadingWrapper>
        <div class="map-placeholder"></div>
      </LoadingWrapper>
    {:then joinedMapData}
      <SVGMap
        features={joinedMapData}
        on:click={onClear}
        aspectRatio={4 / 2.5}
        projection={geoAlbersUsaPr}
      >
        <SVGPolygonLayer
          fill={(d) => fillFunc(d.properties, metric)}
          strokeWidth={config.map.strokeWidth}
          highlightFeature={{ fips: currentState }}
          hoverStroke={config.map.hoverStroke}
          hoverStrokeWidth={config.map.hoverStrokeWidth}
          on:click={handleStateClick}
          on:mousemove={handleStateHover}
          on:mouseout={(e) => (tooltip = undefined)}
          on:bgclick={onClear}
        />
      </SVGMap>
    {/await}
  {:else}
    <Tilemap
      {data}
      shape={mapType === "hex" ? mapType : "rect"}
      {featureOption}
      fill={(d) => fillFunc(d, metric)}
      strokeWidth={config.map.strokeWidth}
      highlightFeature={currentState}
      hoverStroke={config.map.hoverStroke}
      hoverStrokeWidth={config.map.hoverStrokeWidth}
      on:bgclick={onClear}
      on:click={handleStateClick}
      on:mousemove={handleStateHover}
      on:mouseout={(e) => (tooltip = undefined)}
    />
  {/if}
</div>
<div class="clear-selection-wrapper">
  <button
    onclick={(e) => {
      // logClickToGA(e.target, "clear-selection-click");
      onClear();
    }}
    style:visibility={currentState ? "visible" : "hidden"}>Clear state selection</button
  >
</div>
{#if tooltip}
  <Tooltip x={tooltip.x} y={tooltip.y} size="large">
    {#if tooltip.props.state}
      <h5>{tooltip.props.state}</h5>
    {/if}
    {#if tooltipColumns}
      <p>
        In {tooltip.props.state}, {config.columns
          .get(tooltipColumns[2])
          .format(tooltip.props[tooltipColumns[2]])} consumers ({config.columns
          .get(tooltipColumns[0])
          .format(tooltip.props[tooltipColumns[0]]).replace("%", " percent")}) are at least 60 days past due on
        their credit card bill, a <strong>{config.columns
          .get(tooltipColumns[1])
          .format(tooltip.props[tooltipColumns[1]]).replace("%", " percent")}</strong> increase from 2022.
      </p>
    {/if}
  </Tooltip>
{/if}

<style>
  .clear-selection-wrapper {
    display: flex;
    justify-content: flex-end;
  }

  .clear-selection-wrapper button {
    appearance: none;
    border: none;
    background: none;
    color: inherit;
    border-bottom: solid 1px var(--color-gray-shade-darkest);
    font-style: italic;
    font-family: inherit;
    cursor: pointer;
    outline: none;
    margin-top: 0.5em;
  }
  .clear-selection-wrapper button:focus {
    outline: var(--color-yellow) solid 2px;
    outline-offset: var(--spacing-2);
  }

  .map-placeholder {
    width: 100%;
    padding-top: 62.5%;
    background-color: var(--color-gray-shade-lightest);
  }
  .legend-labels {
    display: flex;
    font-style: italic;
    justify-content: space-between;
    font-size: var(--font-size-small);
    color: var(--color-gray-shade-darkest);
    margin-bottom: var(--spacing-2);
  }
</style>
